import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Client, CreateClientRequest, UpdateClientRequest,
  Project, ProjectStatus, CreateProjectRequest, UpdateProjectRequest,
  Repository,
} from '../types';
import clientService from '../services/clientService';
import projectService from '../services/projectService';
import repositoryService from '../services/repositoryService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageHeader, Badge, ModalOverlay } from '../components/ui';

/* ============================================================
   Color identity per client — mapped from initials, six-way
   ============================================================ */

/* ============================================================
   Status visuals
   ============================================================ */

const statusVariant = (s: ProjectStatus): 'green' | 'amber' | 'purple' | 'red' => {
  switch (s) {
    case ProjectStatus.ACTIVE: return 'green';
    case ProjectStatus.PAUSED: return 'amber';
    case ProjectStatus.COMPLETED: return 'purple';
    case ProjectStatus.ARCHIVED: return 'red';
  }
};

const statusLabel = (s: ProjectStatus): string => {
  switch (s) {
    case ProjectStatus.ACTIVE: return 'Aktiv';
    case ProjectStatus.PAUSED: return 'Pausiert';
    case ProjectStatus.COMPLETED: return 'Abgeschlossen';
    case ProjectStatus.ARCHIVED: return 'Archiviert';
  }
};

/* ============================================================
   Editor types — same shape as before
   ============================================================ */

type ClientForm = {
  name: string; contactEmail: string; billingEmail: string; contactPhone: string; notes: string;
  contactPerson: string; addressStreet: string; addressZip: string; addressCity: string; addressCountry: string;
  vatId: string; commercialRegister: string;
  bankAccountHolder: string; bankName: string; bankIban: string; bankBic: string;
  active: boolean;
};
type ProjectForm = { clientId: number; name: string; description: string; status: ProjectStatus; domain: string; active: boolean };

type Editor =
  | { kind: 'client'; mode: 'create'; data: ClientForm }
  | { kind: 'client'; mode: 'edit'; id: number; data: ClientForm }
  | { kind: 'project'; mode: 'create'; data: ProjectForm }
  | { kind: 'project'; mode: 'edit'; id: number; data: ProjectForm };

type Confirm =
  | { kind: 'client'; id: number; name: string }
  | { kind: 'project'; id: number; name: string };

const emptyClient: ClientForm = {
  name: '', contactEmail: '', billingEmail: '', contactPhone: '', notes: '',
  contactPerson: '', addressStreet: '', addressZip: '', addressCity: '', addressCountry: '',
  vatId: '', commercialRegister: '',
  bankAccountHolder: '', bankName: '', bankIban: '', bankBic: '',
  active: true,
};
const emptyProject = (clientId: number): ProjectForm => ({
  clientId, name: '', description: '', status: ProjectStatus.ACTIVE, domain: '', active: true,
});

/** Ensure a URL has a scheme so anchor `href` opens the right place. */
const toHref = (domain: string): string =>
  /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;

/** Display form: strip scheme + trailing slash for readability. */
const prettyDomain = (domain: string): string =>
  domain.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

/* ============================================================
   Icons
   ============================================================ */

const I = {
  Plus: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16M4 12h16" />
    </svg>
  ),
  Search: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="m20 20-3.5-3.5" />
    </svg>
  ),
  Edit: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  Trash: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    </svg>
  ),
  Inbox: () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
    </svg>
  ),
  External: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h5" />
    </svg>
  ),
  Globe: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </svg>
  ),
  Chevron: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
    </svg>
  ),
};

/* ============================================================
   Main component
   ============================================================ */

const Workspace: React.FC = () => {
  usePageTitle('Kunden');

  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [repos, setRepos] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);

  const [editor, setEditor] = useState<Editor | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const newClientBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { load(); }, []);

  const editorOpen = editor !== null;
  useEffect(() => {
    if (editorOpen && modalRef.current) {
      modalRef.current.querySelector<HTMLInputElement>('input, select, textarea')?.focus();
    } else if (!editorOpen) {
      newClientBtnRef.current?.focus();
    }
  }, [editorOpen]);

  const load = async () => {
    try {
      const [c, p, r] = await Promise.all([
        clientService.getAll(),
        projectService.getAll(),
        repositoryService.getAll(),
      ]);
      setClients(c);
      setProjects(p);
      setRepos(r);
    } catch (err) {
      toast.error('Konnte Daten nicht laden');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const projectsByClient = useMemo(() => {
    const map = new Map<number, Project[]>();
    for (const p of projects) {
      const list = map.get(p.clientId) ?? [];
      list.push(p);
      map.set(p.clientId, list);
    }
    return map;
  }, [projects]);

  const reposByProject = useMemo(() => {
    const map = new Map<number, Repository[]>();
    for (const r of repos) {
      const list = map.get(r.projectId) ?? [];
      list.push(r);
      map.set(r.projectId, list);
    }
    return map;
  }, [repos]);

  /* Filtered client list — search across name/email AND nested data */
  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clients.filter((c) => {
      if (activeOnly && !c.active) return false;
      if (!q) return true;
      if (c.name.toLowerCase().includes(q)) return true;
      if (c.contactEmail?.toLowerCase().includes(q)) return true;
      if (c.billingEmail?.toLowerCase().includes(q)) return true;
      const ps = projectsByClient.get(c.id) ?? [];
      if (ps.some((p) => p.name.toLowerCase().includes(q))) return true;
      if (ps.some((p) => (reposByProject.get(p.id) ?? []).some((r) =>
        r.name.toLowerCase().includes(q) || r.gitUrl.toLowerCase().includes(q),
      ))) return true;
      return false;
    });
  }, [clients, query, activeOnly, projectsByClient, reposByProject]);

  /* ---------- submit ---------- */

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editor) return;
    try {
      if (editor.kind === 'client') {
        const base: CreateClientRequest = {
          name: editor.data.name,
          contactEmail: editor.data.contactEmail || null,
          billingEmail: editor.data.billingEmail || null,
          contactPhone: editor.data.contactPhone || null,
          notes: editor.data.notes || null,
          contactPerson: editor.data.contactPerson || null,
          addressStreet: editor.data.addressStreet || null,
          addressZip: editor.data.addressZip || null,
          addressCity: editor.data.addressCity || null,
          addressCountry: editor.data.addressCountry || null,
          vatId: editor.data.vatId || null,
          commercialRegister: editor.data.commercialRegister || null,
          bankAccountHolder: editor.data.bankAccountHolder || null,
          bankName: editor.data.bankName || null,
          bankIban: editor.data.bankIban || null,
          bankBic: editor.data.bankBic || null,
        };
        if (editor.mode === 'create') {
          await clientService.create(base);
          toast.success('Kunde angelegt');
        } else {
          const payload: UpdateClientRequest = { ...base, active: editor.data.active };
          await clientService.update(editor.id, payload);
          toast.success('Kunde aktualisiert');
        }
      } else if (editor.kind === 'project') {
        if (editor.mode === 'create') {
          const payload: CreateProjectRequest = {
            clientId: editor.data.clientId,
            name: editor.data.name,
            description: editor.data.description || null,
            status: editor.data.status,
            domain: editor.data.domain || null,
          };
          await projectService.create(payload);
          toast.success('Projekt angelegt');
        } else {
          const payload: UpdateProjectRequest = {
            clientId: editor.data.clientId,
            name: editor.data.name,
            description: editor.data.description || null,
            status: editor.data.status,
            domain: editor.data.domain || null,
            active: editor.data.active,
          };
          await projectService.update(editor.id, payload);
          toast.success('Projekt aktualisiert');
        }
      }
      setEditor(null);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Speichern fehlgeschlagen');
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.kind === 'client') await clientService.delete(confirm.id);
      else await projectService.delete(confirm.id);
      toast.success('Gelöscht');
      setConfirm(null);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Löschen fehlgeschlagen');
    }
  };

  const updateField = <K extends keyof (ClientForm & ProjectForm)>(
    key: K,
    value: (ClientForm & ProjectForm)[K],
  ) => {
    setEditor((prev) => (prev ? ({ ...prev, data: { ...prev.data, [key]: value } } as Editor) : prev));
  };

  /* ---------- render ---------- */

  const totalProjects = projects.length;
  const totalRepos = repos.length;

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <PageHeader
        title="Kunden"
        action={
          <button
            ref={newClientBtnRef}
            onClick={() => setEditor({ kind: 'client', mode: 'create', data: { ...emptyClient } })}
            className="btn-primary inline-flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-lg hover:bg-brand-700 font-semibold text-sm shadow-sm"
          >
            <I.Plus /> Neuer Kunde
          </button>
        }
      />

      {/* Stat strip + toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8 animate-fade-up">
        <div className="flex items-baseline gap-5 nums-tabular">
          <Stat label="Kunden" value={clients.length} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>·</span>
          <Stat label="Projekte" value={totalProjects} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>·</span>
          <Stat label="Repositories" value={totalRepos} />
        </div>

        <div className="sm:ml-auto flex items-center gap-2">
          <div
            className="relative flex items-center"
            style={{
              background: 'var(--surface-bg)',
              border: '1px solid var(--surface-border)',
              borderRadius: 10,
            }}
          >
            <span className="pl-3" style={{ color: 'var(--text-muted)' }}><I.Search /></span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suche Kunde, Projekt, Repo…"
              className="bg-transparent border-0 outline-none px-2.5 py-2 text-sm w-56 sm:w-64"
              style={{ color: 'var(--text-primary)' }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="pr-3 text-xs"
                style={{ color: 'var(--text-muted)' }}
                aria-label="Suche löschen"
              >
                ✕
              </button>
            )}
          </div>

          <button
            onClick={() => setActiveOnly((v) => !v)}
            className="text-xs px-3 py-2 rounded-lg font-medium"
            style={{
              background: activeOnly ? 'var(--badge-green-bg)' : 'var(--surface-bg)',
              color: activeOnly ? 'var(--badge-green-text)' : 'var(--text-secondary)',
              border: '1px solid',
              borderColor: activeOnly ? 'var(--badge-green-text)' : 'var(--surface-border)',
            }}
          >
            Nur aktive
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div
          className="rounded-xl overflow-hidden"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}
        >
          {[0, 1].map((i) => (
            <div
              key={i}
              className="px-5 py-5"
              style={{ borderBottom: i === 0 ? '1px solid var(--surface-border)' : 'none' }}
            >
              <div className="skeleton h-3 w-32 mb-3"></div>
              <div className="skeleton h-4 w-2/3 mb-2"></div>
              <div className="skeleton h-4 w-1/2"></div>
            </div>
          ))}
        </div>
      ) : filteredClients.length === 0 ? (
        <EmptyState
          hasQuery={Boolean(query) || activeOnly}
          onCreate={() => setEditor({ kind: 'client', mode: 'create', data: { ...emptyClient } })}
        />
      ) : (
        <div className="space-y-3 animate-fade-up">
          {filteredClients.map((c) => {
            const cProjects = projectsByClient.get(c.id) ?? [];
            const visibleProjects = activeOnly ? cProjects.filter((p) => p.active) : cProjects;
            return (
              <ClientGroup
                key={c.id}
                client={c}
                projects={visibleProjects}
                onEditClient={() => setEditor({
                  kind: 'client', mode: 'edit', id: c.id,
                  data: {
                    name: c.name,
                    contactEmail: c.contactEmail ?? '',
                    billingEmail: c.billingEmail ?? '',
                    contactPhone: c.contactPhone ?? '',
                    notes: c.notes ?? '',
                    contactPerson: c.contactPerson ?? '',
                    addressStreet: c.addressStreet ?? '',
                    addressZip: c.addressZip ?? '',
                    addressCity: c.addressCity ?? '',
                    addressCountry: c.addressCountry ?? '',
                    vatId: c.vatId ?? '',
                    commercialRegister: c.commercialRegister ?? '',
                    bankAccountHolder: c.bankAccountHolder ?? '',
                    bankName: c.bankName ?? '',
                    bankIban: c.bankIban ?? '',
                    bankBic: c.bankBic ?? '',
                    active: c.active,
                  },
                })}
                onDeleteClient={() => setConfirm({ kind: 'client', id: c.id, name: c.name })}
                onAddProject={() => setEditor({ kind: 'project', mode: 'create', data: emptyProject(c.id) })}
                onEditProject={(p) => setEditor({
                  kind: 'project', mode: 'edit', id: p.id,
                  data: {
                    clientId: p.clientId,
                    name: p.name,
                    description: p.description ?? '',
                    status: p.status,
                    domain: p.domain ?? '',
                    active: p.active,
                  },
                })}
                onDeleteProject={(p) => setConfirm({ kind: 'project', id: p.id, name: p.name })}
              />
            );
          })}
        </div>
      )}

      {/* Editor modal */}
      {editor && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="workspace-modal-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setEditor(null); }}
          innerRef={modalRef}
        >
          <div
            className="rounded-xl p-6 w-full max-w-md"
            style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}
          >
            <h2 id="workspace-modal-title" className="text-lg font-semibold mb-1 tracking-tight">
              {modalTitle(editor)}
            </h2>
            <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
              {modalSubtitle(editor)}
            </p>
            <form onSubmit={submit}>
              {editor.kind === 'client' && (
                <ClientFields data={editor.data} mode={editor.mode} onChange={updateField} />
              )}
              {editor.kind === 'project' && (
                <ProjectFields data={editor.data} mode={editor.mode} clients={clients} onChange={updateField} />
              )}

              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button" onClick={() => setEditor(null)}
                  className="px-4 py-2 rounded-lg text-sm"
                  style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn-primary px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-semibold"
                >
                  {editor.mode === 'edit' ? 'Speichern' : 'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* Confirm delete */}
      {confirm && (
        <ModalOverlay
          role="alertdialog" ariaLabelledBy="ws-del-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirm(null); }}
        >
          <div className="rounded-xl p-6 w-full max-w-sm" style={{ background: 'var(--surface-bg)' }}>
            <h2 id="ws-del-title" className="text-base font-semibold mb-2 tracking-tight" style={{ color: 'var(--text-primary)' }}>
              {confirmTitle(confirm.kind)}
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Möchtest du <strong>{confirm.name}</strong> wirklich löschen?
              <span className="block mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                Alle untergeordneten Einträge werden ebenfalls gelöscht.
              </span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)} autoFocus
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
              >
                Abbrechen
              </button>
              <button
                onClick={doDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
              >
                Löschen
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

/* ============================================================
   Stat — label + tabular number, paired
   ============================================================ */

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <span className="flex items-baseline gap-1.5">
    <span
      className="text-xl font-extrabold tracking-tight"
      style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
    >
      {value}
    </span>
    <span
      className="text-[11px] font-semibold uppercase tracking-widest"
      style={{ color: 'var(--text-muted)' }}
    >
      {label}
    </span>
  </span>
);

/* ============================================================
   ClientGroup — uppercase header + project rows + tiny add link
   ============================================================ */

const ClientGroup: React.FC<{
  client: Client;
  projects: Project[];
  onEditClient: () => void;
  onDeleteClient: () => void;
  onAddProject: () => void;
  onEditProject: (p: Project) => void;
  onDeleteProject: (p: Project) => void;
}> = ({ client, projects, onEditClient, onDeleteClient, onAddProject, onEditProject, onDeleteProject }) => (
  <div
    className="group/card rounded-xl overflow-hidden transition-shadow"
    style={{
      background: 'var(--surface-bg)',
      border: '1px solid var(--surface-border)',
      boxShadow: 'var(--surface-shadow)',
    }}
  >
    {/* Client header — entire title strip is clickable to edit */}
    <div
      className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 transition-colors"
      style={{ borderBottom: '1px solid var(--surface-border)' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--table-header-bg)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <button
        type="button"
        onClick={onEditClient}
        title="Kunde bearbeiten"
        className="flex items-baseline gap-2.5 min-w-0 text-left flex-1 -my-1 py-1"
      >
        <h3
          className="text-sm font-bold tracking-tight truncate"
          style={{ color: 'var(--text-primary)' }}
        >
          {client.name}
        </h3>
        {!client.active && <Badge variant="red">Inaktiv</Badge>}
        <span className="text-[12px] truncate" style={{ color: 'var(--text-muted)' }}>
          {client.contactEmail || client.contactPhone || 'Keine Kontaktdaten'}
        </span>
      </button>
      <div className="flex items-center shrink-0 opacity-40 group-hover/card:opacity-100 transition-opacity">
        <RowAction onClick={onEditClient} title="Kunde bearbeiten"><I.Edit /></RowAction>
        <RowAction onClick={onDeleteClient} title="Kunde löschen" danger><I.Trash /></RowAction>
      </div>
    </div>

    {/* Project rows */}
    {projects.length === 0 ? (
      <div className="text-[12px] px-4 sm:px-5 py-3" style={{ color: 'var(--text-muted)' }}>
        Noch keine Projekte.
      </div>
    ) : (
      <div>
        {projects.map((p, i) => (
          <ProjectRow
            key={p.id}
            project={p}
            isFirst={i === 0}
            onEdit={() => onEditProject(p)}
            onDelete={() => onDeleteProject(p)}
          />
        ))}
      </div>
    )}

    {/* Add project — subtle text link */}
    <button
      onClick={onAddProject}
      className="w-full inline-flex items-center gap-1.5 text-[12px] px-4 sm:px-5 py-2.5 transition-colors text-left"
      style={{ color: 'var(--text-muted)', borderTop: '1px solid var(--surface-border)' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--text-primary)';
        e.currentTarget.style.background = 'var(--table-header-bg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--text-muted)';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <I.Plus /> Projekt hinzufügen
    </button>
  </div>
);

/* ============================================================
   ProjectRow — kompakte, aligned Zeile
   Spalten: Name (1fr) · Status (auto) · Domain (auto/min-0) · Actions (auto)
   ============================================================ */

const ProjectRow: React.FC<{
  project: Project;
  isFirst: boolean;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ project, isFirst, onEdit, onDelete }) => (
  <div
    className="group/row relative grid items-center gap-3 px-4 sm:px-5 py-2.5 transition-colors"
    style={{
      gridTemplateColumns: 'minmax(140px,1fr) auto minmax(0,auto) auto auto',
      borderTop: isFirst ? 'none' : '1px solid var(--surface-border)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--table-row-hover)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    {/* Stretched link — fills the row; siblings with z-10 stay interactive */}
    <Link
      to={`/projects/${project.id}`}
      aria-label={`Projekt ${project.name} öffnen`}
      title="Board öffnen"
      className="absolute inset-0 z-0"
    />

    <span
      className="relative z-[1] text-[13px] font-semibold tracking-tight truncate pointer-events-none transition-colors"
      style={{ color: 'var(--text-primary)' }}
    >
      {project.name}
    </span>

    <div className="relative z-[1] flex items-center gap-1.5 pointer-events-none">
      <Badge variant={statusVariant(project.status)}>{statusLabel(project.status)}</Badge>
      {!project.active && <Badge variant="red">Inaktiv</Badge>}
    </div>

    <div className="relative z-10 min-w-0">
      {project.domain ? (
        <a
          href={toHref(project.domain)}
          target="_blank"
          rel="noopener noreferrer"
          title={`Prod öffnen · ${prettyDomain(project.domain)}`}
          className="inline-flex items-center gap-1.5 text-[12px] font-mono max-w-full transition-colors"
          style={{ color: 'var(--text-secondary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--badge-green-text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
        >
          <I.Globe />
          <span className="truncate">{prettyDomain(project.domain)}</span>
          <I.External />
        </a>
      ) : (
        <span className="text-[12px] pointer-events-none" style={{ color: 'var(--text-placeholder)' }}>—</span>
      )}
    </div>

    <div className="relative z-10 flex items-center justify-end gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
      <RowAction onClick={onEdit} title="Projekt bearbeiten"><I.Edit /></RowAction>
      <RowAction onClick={onDelete} title="Projekt löschen" danger><I.Trash /></RowAction>
    </div>

    <div
      className="relative z-[1] pointer-events-none transition-transform group-hover/row:translate-x-0.5"
      style={{ color: 'var(--text-muted)' }}
      aria-hidden="true"
    >
      <I.Chevron />
    </div>
  </div>
);

/* ============================================================
   RowAction — winziger, gedämpfter Icon-Button
   ============================================================ */

const RowAction: React.FC<{
  onClick: () => void; title: string; danger?: boolean; children: React.ReactNode;
}> = ({ onClick, title, danger, children }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    className="h-7 w-7 rounded inline-flex items-center justify-center transition-colors"
    style={{ color: danger ? 'var(--badge-red-text)' : 'var(--text-muted)' }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = danger ? 'var(--badge-red-bg)' : 'var(--table-header-bg)';
    }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    {children}
  </button>
);

/* ============================================================
   Empty state
   ============================================================ */

const EmptyState: React.FC<{ hasQuery: boolean; onCreate: () => void }> = ({ hasQuery, onCreate }) => (
  <div
    className="rounded-2xl py-16 px-6 text-center animate-fade-up"
    style={{
      background: 'var(--surface-bg)',
      border: '1px dashed var(--surface-border)',
    }}
  >
    <div
      className="mx-auto mb-5 h-16 w-16 rounded-2xl flex items-center justify-center"
      style={{ background: 'var(--table-header-bg)', color: 'var(--text-muted)' }}
    >
      <I.Inbox />
    </div>
    <h3 className="text-lg font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
      {hasQuery ? 'Keine Treffer' : 'Noch keine Kunden'}
    </h3>
    <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
      {hasQuery
        ? 'Suche anpassen oder Filter zurücksetzen.'
        : 'Lege deinen ersten Kunden an, um Projekte und Repositories zu organisieren.'}
    </p>
    {!hasQuery && (
      <button
        onClick={onCreate}
        className="btn-primary mt-5 inline-flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-lg hover:bg-brand-700 font-semibold text-sm"
      >
        <I.Plus /> Ersten Kunden anlegen
      </button>
    )}
  </div>
);

/* ============================================================
   Form field subcomponents
   ============================================================ */

const labelCls = 'block text-xs font-semibold mb-1.5 uppercase tracking-wider';
const labelStyle = { color: 'var(--text-muted)' } as const;
const inputCls = 'w-full p-2.5 border rounded-lg input-themed text-sm';

const ClientFields: React.FC<{
  data: ClientForm; mode: 'create' | 'edit';
  onChange: (k: keyof ClientForm, v: any) => void;
}> = ({ data, mode, onChange }) => (
  <>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Name *</label>
      <input type="text" required value={data.name}
        onChange={(e) => onChange('name', e.target.value)} className={inputCls} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      <div>
        <label className={labelCls} style={labelStyle}>E-Mail</label>
        <input type="email" value={data.contactEmail}
          onChange={(e) => onChange('contactEmail', e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Telefon</label>
        <input type="text" value={data.contactPhone}
          onChange={(e) => onChange('contactPhone', e.target.value)} className={inputCls} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} style={labelStyle}>E-Mail für Rechnungen</label>
        <input type="email" value={data.billingEmail} placeholder="leer = E-Mail von oben"
          onChange={(e) => onChange('billingEmail', e.target.value)} className={inputCls} />
      </div>
    </div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Ansprechpartner</label>
      <input type="text" value={data.contactPerson} placeholder="Herrn Max Mustermann"
        onChange={(e) => onChange('contactPerson', e.target.value)} className={inputCls} />
    </div>

    <div className="mb-2 text-xs font-semibold uppercase tracking-wide" style={labelStyle}>Anschrift</div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Straße &amp; Nr.</label>
      <input type="text" value={data.addressStreet}
        onChange={(e) => onChange('addressStreet', e.target.value)} className={inputCls} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
      <div>
        <label className={labelCls} style={labelStyle}>PLZ</label>
        <input type="text" value={data.addressZip}
          onChange={(e) => onChange('addressZip', e.target.value)} className={inputCls} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} style={labelStyle}>Ort</label>
        <input type="text" value={data.addressCity}
          onChange={(e) => onChange('addressCity', e.target.value)} className={inputCls} />
      </div>
    </div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Land</label>
      <input type="text" value={data.addressCountry} placeholder="Deutschland"
        onChange={(e) => onChange('addressCountry', e.target.value)} className={inputCls} />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      <div>
        <label className={labelCls} style={labelStyle}>USt-IdNr.</label>
        <input type="text" value={data.vatId} placeholder="DE123456789"
          onChange={(e) => onChange('vatId', e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Handelsregister (HRB)</label>
        <input type="text" value={data.commercialRegister} placeholder="HRB 12345"
          onChange={(e) => onChange('commercialRegister', e.target.value)} className={inputCls} />
      </div>
    </div>

    <div className="mb-2 text-xs font-semibold uppercase tracking-wide" style={labelStyle}>Bankverbindung</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      <div>
        <label className={labelCls} style={labelStyle}>Kontoinhaber</label>
        <input type="text" value={data.bankAccountHolder}
          onChange={(e) => onChange('bankAccountHolder', e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Bank</label>
        <input type="text" value={data.bankName}
          onChange={(e) => onChange('bankName', e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>IBAN</label>
        <input type="text" value={data.bankIban}
          onChange={(e) => onChange('bankIban', e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>BIC</label>
        <input type="text" value={data.bankBic}
          onChange={(e) => onChange('bankBic', e.target.value)} className={inputCls} />
      </div>
    </div>

    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Notizen</label>
      <textarea rows={3} value={data.notes}
        onChange={(e) => onChange('notes', e.target.value)} className={inputCls} />
    </div>
    {mode === 'edit' && (
      <div className="mb-4">
        <label className="flex items-center text-sm" style={{ color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={data.active}
            onChange={(e) => onChange('active', e.target.checked)} className="mr-2" />
          Aktiv
        </label>
      </div>
    )}
  </>
);

const ProjectFields: React.FC<{
  data: ProjectForm; mode: 'create' | 'edit'; clients: Client[];
  onChange: (k: keyof ProjectForm, v: any) => void;
}> = ({ data, mode, clients, onChange }) => (
  <>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Kunde *</label>
      <select required value={data.clientId}
        onChange={(e) => onChange('clientId', Number(e.target.value))} className={inputCls}>
        {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
      </select>
    </div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Name *</label>
      <input type="text" required value={data.name}
        onChange={(e) => onChange('name', e.target.value)} className={inputCls} />
    </div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Beschreibung</label>
      <textarea rows={3} value={data.description}
        onChange={(e) => onChange('description', e.target.value)} className={inputCls} />
    </div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Status</label>
      <select value={data.status}
        onChange={(e) => onChange('status', e.target.value as ProjectStatus)} className={inputCls}>
        {Object.values(ProjectStatus).map((s) => (
          <option key={s} value={s}>{statusLabel(s)}</option>
        ))}
      </select>
    </div>
    <div className="mb-4">
      <label className={labelCls} style={labelStyle}>Prod-Domain</label>
      <input type="text" value={data.domain}
        placeholder="example.com"
        onChange={(e) => onChange('domain', e.target.value)}
        className={`${inputCls} font-mono`} />
      <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
        Schema optional – fehlt es, wird beim Öffnen <span className="font-mono">https://</span> ergänzt.
      </p>
    </div>
    {mode === 'edit' && (
      <div className="mb-4">
        <label className="flex items-center text-sm" style={{ color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={data.active}
            onChange={(e) => onChange('active', e.target.checked)} className="mr-2" />
          Aktiv
        </label>
      </div>
    )}
  </>
);

/* ============================================================
   Labels
   ============================================================ */

const modalTitle = (e: Editor): string => {
  const noun = e.kind === 'client' ? 'Kunde' : 'Projekt';
  return e.mode === 'edit' ? `${noun} bearbeiten` : `Neue${e.kind === 'client' ? 'r' : 's'} ${noun}`;
};

const modalSubtitle = (e: Editor): string => {
  if (e.mode === 'edit') return 'Änderungen werden sofort übernommen.';
  if (e.kind === 'client') return 'Stammdaten für einen neuen Kunden.';
  return 'Neues Projekt für einen bestehenden Kunden.';
};

const confirmTitle = (k: Confirm['kind']): string =>
  k === 'client' ? 'Kunde löschen' : 'Projekt löschen';

export default Workspace;
